import React, { useEffect, useMemo } from 'react';

import Cookie from 'js-cookie';

import { COOKIE_AST_TOKEN_JS, COOKIE_AST_SIGNUP_SUCCESS, KEYS } from '../../../../../../constants/Common';
import { AUTH_FUNCTIONAL } from '../../../../../../constants/Metrics';


import AuthService from '../../../../../../services/Api/Auth';
import ExperimentService from '../../../../../../services/Api/ExperimentService';
import MetricsService from '../../../../../../services/Api/Metrics';
import UserService from '../../../../../../services/Api/User';
import Masks from '../../../../../../services/Masks';
import Url from '../../../../../../services/Url';

import { IReducerActionWithField } from '../../../../../Hooks/Context/connect';
import connectAuth, { IAuthMapStateToProps, IConnectedProps } from '../../hooks/connectAuth';

import Button, { IGradient } from '../../../Common/Form/Button';
import LoadingSvg from '../../../Common/LoadingSvg';
import { Steps } from '../../Constants';
import {
    multipleActions,
    actionRedirectAuth,
    actionRenderPayment,
    Types as ContainerTypes,

    IPagarmeProps,
    ICommunication
} from '../../Store/ContainerReducer';
import { IAuthState } from '../../Store/Reducers';
import {
    actionButton,
    Types as SignUpTypes,

    IKeyDown,
    IBirthDateState,
    IBirthHourState,
    IPersonalDataState
} from '../../Store/SignUpReducer';
import { validateDateValue } from './BirthData/BirthDateSelector/BirthDateSelector';
import { GENDER, REFERENCING } from './PersonalData/InputGender';

interface DataLayerObject {
    event: string;
    [key: string]: any;
}

declare global {
    interface Window {
        dataLayer?: DataLayerObject[];
    }
}

interface ISignUpButtonProps {
    step: string,
    theme: string,
    origin: string,
    period: string,
    loading: boolean,
    keyDown: IKeyDown,
    useUSFormat: boolean,
    birthPlaceId: string,
    redirectToAuth: number,
    pagarme: IPagarmeProps,
    birthPlaceError: string,
    birthDate: IBirthDateState,
    birthHour: IBirthHourState,
    buttonGradient?: IGradient,
    checkedWithoutHour: boolean,
    keepSubmitEnabled?: boolean,
    communication: ICommunication,
    personalData: IPersonalDataState,
}

interface ISignUpError {
    field: string,
    message: string,
    dispatchType?: string
}

type TSignUpButtonProps = IConnectedProps<ISignUpButtonProps>;

const SignUpButton: React.FC<TSignUpButtonProps> = ({ intl, dispatch, ...props }) => {
    const $AuthService = new AuthService();
    const $UserService = new UserService();
    const $MetricsService = new MetricsService();
    const $ExperimentService = new ExperimentService();

    const {
        step,
        theme,
        origin,
        period,
        pagarme,
        loading,
        birthDate,
        birthHour,
        useUSFormat,
        personalData,
        birthPlaceId,
        communication,
        redirectToAuth,
        buttonGradient,
        birthPlaceError,
        keepSubmitEnabled,
        checkedWithoutHour
    } = props;

    const isSynastryInvite = theme === 'SynastryInvite';

    const synastryInviteButton = step === Steps.BIRTH_DATA
        ? 'synastry.invite.authFunctional.buttons.birthData'
        : 'synastry.invite.authFunctional.buttons.personalData';

    const isNonBinary = personalData.gender.value === GENDER.notBinary;

    const finalReferencing = (() => {
        if ((useUSFormat && isNonBinary) || personalData.gender.value === GENDER.female) {
            return REFERENCING[GENDER.female];
        } if (!personalData.referencing.value) {
            return REFERENCING[GENDER.male];
        }
        return personalData.referencing.value;
    })();

    const {
        buttonBirthDataText,
        buttonPersonalDataText
    } = communication;

    const { selectedHour } = birthHour;

    const [hasError, hasValue] = useMemo(() => {
        if (step === Steps.BIRTH_DATA) {
            const hasLocalError = (!!birthDate.error || !!birthPlaceError);
            const hasLocalValue = (birthDate.date !== null && birthDate.date.isValid()) && !!birthPlaceId;

            return [hasLocalError, hasLocalValue];
        }

        if (step === Steps.PERSONAL_DATA) {
            const { name, email, password, gender, referencing } = personalData;

            const hasLocalError = !!(name.error || email.error || gender.error || password.error || referencing.error);
            const hasLocalValue = !!(name.value && email.value && password.value);

            return [hasLocalError, hasLocalValue];
        }

        return [false, false];
    }, [step, birthDate.date, birthDate.error, birthPlaceError, birthPlaceId, personalData]);

    const buttonTitle = useMemo(() => {
        if (isSynastryInvite) {
            return intl.formatMessage({ id: synastryInviteButton });
        }

        if (step === Steps.BIRTH_DATA && buttonBirthDataText) {
            return buttonBirthDataText;
        }

        if (step === Steps.PERSONAL_DATA && buttonPersonalDataText) {
            return buttonPersonalDataText;
        }

        return intl.formatMessage({ id: 'authFunctional.button' });
    }, [intl, step, buttonBirthDataText, buttonPersonalDataText, isSynastryInvite, synastryInviteButton]);

    const disabled = useMemo(() => {
        if (keepSubmitEnabled && step === Steps.BIRTH_DATA) return false;

        if (step === Steps.BIRTH_DATA) {
            const isTimeValid = checkedWithoutHour ||
                (!checkedWithoutHour && (!!selectedHour && !birthHour.error));

            return hasError || !hasValue || !isTimeValid;
        }

        if (step === Steps.PERSONAL_DATA) {
            const { consentTerms } = personalData;

            return !consentTerms || (hasError || !hasValue);
        }

        return true;
    }, [keepSubmitEnabled, birthHour.error, selectedHour, checkedWithoutHour, hasError, hasValue, personalData, step]);

    const tabIndex = !disabled ? 0 : -1;

    const getSelectedHour = () => Masks.convert12to24(selectedHour, period, useUSFormat);

    const sendSignUpMetric = (token: string) => $MetricsService.setMetricsWithJWT(
        token,
        AUTH_FUNCTIONAL.FEATURE,
        window.location.pathname,
        AUTH_FUNCTIONAL.ACTION_SIGNUP,
        ''
    );

    const generateTypeError = (error: ISignUpError) => {
        const emptyAction = [{ type: '', data: null }];
        if (Array.isArray(error)) {
            return [
                {
                    type: SignUpTypes.CHANGE_PERSONAL_FIELD_DATA,
                    field: error[0].dispatchType,
                    data: { error: error[0].message }
                },
                {
                    type: SignUpTypes.CHANGE_PERSONAL_FIELD_DATA,
                    field: error[1].dispatchType,
                    data: { error: error[1].message }
                }
            ];
        }

        const dispachsType: Record<string, IReducerActionWithField[]> = {
            email: [{
                type: SignUpTypes.CHANGE_PERSONAL_FIELD_DATA,
                field: error.dispatchType,
                data: { error: error.message }
            }],

            emailDeleted: [{
                type: SignUpTypes.CHANGE_PERSONAL_FIELD_DATA,
                field: error.dispatchType,
                data: { error: error.message }
            }],

            birthPlace: [{
                type: SignUpTypes.CHANGE_PLACE_DATA,
                data: { error: error.message }
            }],

            birthDate: [{
                type: SignUpTypes.CHANGE_DATE_DATA,
                data: { error: error.message }
            }]
        };

        if (!error.dispatchType) return emptyAction;

        return dispachsType[error.dispatchType] || emptyAction;
    };

    const dispatchError = (error: ISignUpError) => {
        const generalDispatchError = {
            type: ContainerTypes.HANDLE_ERROR,
            data: intl.formatMessage({ id: 'authFunctional.requestError' })
        };

        if (!Array.isArray(error) && !!error.field) {
            dispatch(generalDispatchError);
            return;
        }

        const typeError = generateTypeError(error);

        multipleActions(dispatch, [
            generalDispatchError,
            ...typeError
        ]);
    };

    const handleSignUpError = (error: ISignUpError) => {
        dispatch({
            type: SignUpTypes.CHANGE_SIGNUP_LOADING,
            data: false
        });

        if (error.message) {
            dispatchError({
                ...error,
                message: intl.formatMessage({ id: error.message })
            });

            return;
        }

        dispatchError(error);
    };

    const handleUserPayment = (token: string) => {
        if (!pagarme.amount) {
            Cookie.set(COOKIE_AST_TOKEN_JS, token, {
                expires: 365
            });

            Cookie.set(COOKIE_AST_SIGNUP_SUCCESS, '1');

            if (redirectToAuth) {
                actionRedirectAuth(token);
                return;
            }

            const redirectUrl = Url.base(intl.formatMessage({ id: 'authFunctional.redirectRoute' }));
            Url.location(redirectUrl);
            return;
        }

        if (window.dataLayer) window.dataLayer.push({ 'event': 'signup-success' });

        actionRenderPayment(dispatch, token, pagarme.urlReference);
    };

    const signUpUser = async () => {
        if (!birthPlaceId) {
            dispatch({
                type: ContainerTypes.HANDLE_ERROR,
                data: {
                    error: intl.formatMessage({ id: 'authFunctional.inputCity.invalid' })
                }
            });
            return;
        }

        if (!birthDate.date) {
            return;
        }


        const { name, email, gender, password, consentTerms, emailSubscription } = personalData;
        const finalHour = checkedWithoutHour ? '' : getSelectedHour();

        const currentDate = window.moment(new Date(birthDate.date.year(), birthDate.date.month(), birthDate.date.date(), 12, 0, 0));
        const unixTimeStamp = parseInt(currentDate.format('X'));

        try {
            const token = await $AuthService.signUpFuctional({
                name: name.value,
                email: email.value,
                password: password.value,
                referencing: finalReferencing,
                consentSubscription: consentTerms,
                gender: gender.value || GENDER.female,

                birthHour: finalHour,
                birthDate: unixTimeStamp,
                birthPlace: birthPlaceId,

                emailSubscription,
                origin
            });

            $UserService.addTarotCredit(personalData.email.value, token);

            sendSignUpMetric(token);

            handleUserPayment(token);
        } catch (error) {
            handleSignUpError(error as ISignUpError);
        }
    };

    const handleOnClick = async () => {
        if (step === Steps.BIRTH_DATA) {
            const isValid = await validateBirthData();

            if (!isValid) {
                return;
            }

            const validationResult = validateDateValue(
                birthDate.displayDate,
                birthDate.minDate,
                birthDate.maxDate,
                useUSFormat,
                intl
            );

            actionButton(
                dispatch,
                step,
                {
                    emptyDateField: !validationResult.date,
                    emptyPlaceField: !birthPlaceId,
                    emptyHourField: !checkedWithoutHour && !birthHour.selectedHour
                }
            );

            return;
        }

        multipleActions(dispatch, [
            {
                type: SignUpTypes.CHANGE_SIGNUP_LOADING,
                data: true
            },
            { type: SignUpTypes.CLEAN_ERRORS },
            {
                type: ContainerTypes.HANDLE_ERROR,
                data: ''
            },
            {
                type: ContainerTypes.SHOW_PAYMENT_MODAL
            }
        ]);

        await signUpUser();
    };

    const validateBirthData = () => new Promise<boolean>((resolve) => {
        const validationResult = validateDateValue(
            birthDate.displayDate,
            birthDate.minDate,
            birthDate.maxDate,
            useUSFormat,
            intl
        );

        if (validationResult.error) {
            dispatch({
                type: SignUpTypes.CHANGE_DATE_DATA,
                data: { error: validationResult.error }
            });
            resolve(false);
            return;
        }

        dispatch({
            type: SignUpTypes.CHANGE_DATE_DATA,
            data: { date: validationResult.date, error: '' }
        });

        resolve(true);
    });

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Enter' && !disabled) {
                event.preventDefault();
                handleOnClick();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [disabled, handleOnClick]);

    return (
        <Button
            id="auth-signup"
            loading={loading}
            disabled={disabled}
            tabIndex={tabIndex}
            variation="gradient"
            onClick={handleOnClick}
            gradient={buttonGradient}
        >
            {loading ? <LoadingSvg /> : buttonTitle}
        </Button>
    );
};

const mapStateToProps: IAuthMapStateToProps<ISignUpButtonProps> = (state, isComparison = false) => ((!isComparison)
    ? {
        step: (state as IAuthState).Container.step,
        theme: (state as IAuthState).Container.theme,
        origin: (state as IAuthState).Container.origin,
        pagarme: (state as IAuthState).Container.pagarme,
        personalData: (state as IAuthState).SignUp.personalData,
        loading: (state as IAuthState).SignUp.signUpButtonLoading,
        birthDate: (state as IAuthState).SignUp.birthData.birthDate,
        birthHour: (state as IAuthState).SignUp.birthData.birthHour,
        communication: (state as IAuthState).Container.communication,
        useUSFormat: (state as IAuthState).Container.i18n.useUSFormat,
        redirectToAuth: (state as IAuthState).Container.redirectToAuth,
        buttonGradient: (state as IAuthState).Container.buttonGradient,
        checkedWithoutHour: (state as IAuthState).SignUp.checkedWithoutHour,
        keepSubmitEnabled: (state as IAuthState).Container.keepSubmitEnabled,
        birthPlaceError: (state as IAuthState).SignUp.birthData.birthPlace.error,
        period: (state as IAuthState).SignUp.birthData.birthHour.timeState.period,
        birthPlaceId: (state as IAuthState).SignUp.birthData.birthPlace.place.place_id,
        keyDown: (KEYS.ENTER === (state as IAuthState).SignUp.keyDown.key) ? (state as IAuthState).SignUp.keyDown : ''
    } :
    {
        step: state.step,
        theme: state.theme,
        origin: state.origin,
        period: state.period,
        loading: state.loading,
        keyDown: state.keyDown,
        pagarme: state.pagarme,
        birthDate: state.birthDate,
        birthHour: state.birthHour,
        useUSFormat: state.useUSFormat,
        birthPlaceId: state.birthPlaceId,
        personalData: state.personalData,
        communication: state.communication,
        buttonGradient: state.buttonGradient,
        redirectToAuth: state.redirectToAuth,
        birthPlaceError: state.birthPlaceError,
        keepSubmitEnabled: state.keepSubmitEnabled,
        checkedWithoutHour: state.checkedWithoutHour
    }
);

export default connectAuth(mapStateToProps)(SignUpButton);
